import {
  Card,
  CardContent,
  Checkbox,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { useLayout } from 'context'
import { useLoadingState } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import { PaginationParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { formatDate, formatDateMoment, useDebounce } from 'utils'
import * as controllers from 'controllers'
import {
  MichiganTitleVehicleApplicationReadyToPrintDto,
  MichiganTitleVehicleDto,
  MichiganTitleVehiclePoliceBookDto
} from 'dtos'
import { GreenText, SortableTableCell } from 'components'
import SelectedVehicleCard from './SelectedVehicleCard'
import SelectedVehicleMichiganTitleApplicationPacketCard from './SelectedVehicleMichiganTitleApplicationPacketCard'
import { Form, Formik } from 'formik'
import PoliceBookCard from './PoliceBookCard'
import ApplicationReadyToPrintCard from './ApplicationReadyToPrintCard'
import { enqueueSnackbar } from 'notistack'
import {
  MICHIGAN_TITLES_DOCUMENT_DOWNLOAD_LABEL,
  michiganTitlesCompactCellSx,
  michiganTitlesDateCellSx,
  michiganTitlesDocumentCellSx,
  michiganTitlesLinkSx,
  michiganTitlesMissingCellSx,
  michiganTitlesTableContainerSx,
  michiganTitlesTableSx
} from './michiganTitlesTableStyles'

export default function MichiganTitles() {
  const layout = useLayout()
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingMichiganTitleVehicles: false,
    isSavingMichiganTitleApplicationPacket: false,
    isGettingMichiganTitleVehiclesPoliceBook: false,
    isSavingMichiganTitleVehiclesPoliceBook: false,
    isGettingApplicationReadyToPrint: false,
    isSavingApplicationReadyToPrint: false
  })

  const [michiganTitleVehicles, setMichiganTitleVehicles] = useState<MichiganTitleVehicleDto[]>([])
  const [michiganTitleVehiclesPoliceBook, setMichiganTitleVehiclesPoliceBook] = useState<MichiganTitleVehiclePoliceBookDto[]>(
    []
  )
  const [parameters, setParameters] = useState<PaginationParameters>({
    page: 0,
    pageSize: 10,
    order: 'asc',
    orderBy: 'importerStockNumber'
  })
  const [totalCount, setTotalCount] = useState<number>(0)
  const [selectedVehicle, setSelectedVehicle] = useState<MichiganTitleVehicleDto | undefined>(undefined)
  const [close, setClose] = useState<boolean>(false)
  const [applicationReadyToPrint, setApplicationReadyToPrint] = useState<MichiganTitleVehicleApplicationReadyToPrintDto[]>([])
  const [includeApplicationPacketsReadyForMISoSBundle, setIncludeApplicationPacketsReadyForMISoSBundle] =
    useState<boolean>(false)

  const getMichiganTitleVehicles = useDebounce((parameters: PaginationParameters) => {
    setLoadingState('isGettingMichiganTitleVehicles', true)
    controllers.michiganTitles
      .getMichiganTitleVehicles(parameters)
      .then(res => {
        setMichiganTitleVehicles(res.value)
        setTotalCount(res.totalCount ?? 0)
      })
      .finally(() => {
        setLoadingState('isGettingMichiganTitleVehicles', false)
      })
  }, DEBOUNCE_DELAY)

  const getMichiganTitleVehiclesPoliceBook = () => {
    setLoadingState('isGettingMichiganTitleVehiclesPoliceBook', true)
    controllers.michiganTitles
      .getMichiganTitlePoliceBook()
      .then(res => {
        setMichiganTitleVehiclesPoliceBook(res.value)
      })
      .finally(() => {
        setLoadingState('isGettingMichiganTitleVehiclesPoliceBook', false)
      })
  }

  const getApplicationReadyToPrint = () => {
    setLoadingState('isGettingApplicationReadyToPrint', true)
    controllers.michiganTitles
      .getApplicationReadyToPrint(includeApplicationPacketsReadyForMISoSBundle)
      .then(res => {
        setApplicationReadyToPrint(res.value)
      })
      .finally(() => {
        setLoadingState('isGettingApplicationReadyToPrint', false)
      })
  }

  useEffect(() => {
    getMichiganTitleVehiclesPoliceBook()
  }, [])

  useEffect(() => {
    getApplicationReadyToPrint()
  }, [includeApplicationPacketsReadyForMISoSBundle])

  useEffect(() => {
    if (!getMichiganTitleVehicles) {
      return
    }

    getMichiganTitleVehicles(parameters)
  }, [getMichiganTitleVehicles, parameters])

  useEffect(() => {
    layout.setIsLayoutLoading(
      loadingState.isGettingMichiganTitleVehicles ||
        loadingState.isSavingMichiganTitleApplicationPacket ||
        loadingState.isGettingMichiganTitleVehiclesPoliceBook ||
        loadingState.isSavingMichiganTitleVehiclesPoliceBook ||
        loadingState.isGettingApplicationReadyToPrint ||
        loadingState.isSavingApplicationReadyToPrint
    )
  }, [
    layout,
    loadingState.isGettingMichiganTitleVehicles,
    loadingState.isSavingMichiganTitleApplicationPacket,
    loadingState.isGettingMichiganTitleVehiclesPoliceBook,
    loadingState.isSavingMichiganTitleVehiclesPoliceBook,
    loadingState.isGettingApplicationReadyToPrint,
    loadingState.isSavingApplicationReadyToPrint
  ])

  const handleChangeOrderBy = (orderBy: string) => {
    setParameters(prev => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  return (
    <Grid container spacing={1} alignItems='center'>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography fontWeight='bold' variant='h5'>
              Michigan Titles
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Grid container spacing={1} alignItems='center'>
              <Grid item xs={12}>
                <TableContainer sx={michiganTitlesTableContainerSx}>
                  <Table size='small' sx={michiganTitlesTableSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding='checkbox'></TableCell>

                        <SortableTableCell
                          onChangeSortByDirection={handleChangeOrderBy}
                          propertyName='importerStockNumber'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Importer Stock #
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={handleChangeOrderBy}
                          propertyName='exporterCompany'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Exporter Company
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={handleChangeOrderBy}
                          propertyName='buyerCompany'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Buyer Company
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={handleChangeOrderBy}
                          propertyName='papsNumber'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          PAPS #
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={handleChangeOrderBy}
                          propertyName='_signedBOLCreatedDateTime'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Signed BOL
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={handleChangeOrderBy}
                          propertyName='vin'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          VIN
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={handleChangeOrderBy}
                          propertyName='year'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Year
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={handleChangeOrderBy}
                          propertyName='make'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Make
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={handleChangeOrderBy}
                          propertyName='model'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Model
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={handleChangeOrderBy}
                          propertyName='7501'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          7501
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={handleChangeOrderBy}
                          propertyName='canOwnership'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          CAN Ownership
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={handleChangeOrderBy}
                          propertyName='vehicleCheckInDateTime'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Vehicle Check-In
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={handleChangeOrderBy}
                          propertyName='sentToDOTDateTime'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Sent to DOT
                        </SortableTableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {michiganTitleVehicles.map((vehicle, index) => (
                        <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                          <TableCell padding='checkbox'>
                            <Checkbox
                              checked={vehicle.vehicleGuid === selectedVehicle?.vehicleGuid}
                              disabled={!vehicle._7501FileName || !vehicle._CANOwnershipFileName}
                              indeterminate={!vehicle._7501FileName || !vehicle._CANOwnershipFileName}
                              onChange={() => {
                                setSelectedVehicle(vehicle)
                              }}
                              size='small'
                            />
                          </TableCell>

                          <TableCell sx={michiganTitlesCompactCellSx}>{vehicle.importerStockNumber}</TableCell>

                          <TableCell>{vehicle.exporterCompany?.name}</TableCell>

                          <TableCell>{vehicle.buyerCompany?.name ?? '-'}</TableCell>

                          <TableCell sx={michiganTitlesCompactCellSx}>{vehicle.inboundTripPAPS?.papsNumber}</TableCell>

                          <TableCell sx={michiganTitlesDateCellSx}>
                            {vehicle._SignedBOLCreatedDateTime
                              ? new Date(vehicle._SignedBOLCreatedDateTime).toLocaleDateString()
                              : '-'}
                          </TableCell>

                          <TableCell sx={michiganTitlesCompactCellSx}>{vehicle.vin}</TableCell>

                          <TableCell sx={michiganTitlesCompactCellSx}>{vehicle.year}</TableCell>

                          <TableCell>{vehicle.make}</TableCell>

                          <TableCell>{vehicle.model}</TableCell>

                          {vehicle._7501FileName ? (
                            <TableCell sx={michiganTitlesDocumentCellSx}>
                              <Link
                                onClick={() => {
                                  controllers.vehicles.getVehicleDocumentFileName(vehicle.vehicleGuid, vehicle._7501FileName!)
                                }}
                                sx={michiganTitlesLinkSx}
                              >
                                {MICHIGAN_TITLES_DOCUMENT_DOWNLOAD_LABEL}
                              </Link>
                            </TableCell>
                          ) : (
                            <TableCell sx={michiganTitlesMissingCellSx}>MISSING</TableCell>
                          )}

                          {vehicle._CANOwnershipFileName ? (
                            <TableCell sx={michiganTitlesDocumentCellSx}>
                              <Link
                                onClick={() => {
                                  controllers.vehicles.getVehicleDocumentFileName(
                                    vehicle.vehicleGuid,
                                    vehicle._CANOwnershipFileName!
                                  )
                                }}
                                sx={michiganTitlesLinkSx}
                              >
                                {MICHIGAN_TITLES_DOCUMENT_DOWNLOAD_LABEL}
                              </Link>
                            </TableCell>
                          ) : (
                            <TableCell sx={michiganTitlesMissingCellSx}>MISSING</TableCell>
                          )}

                          <TableCell sx={michiganTitlesDateCellSx}>
                            {vehicle.vehicleCheckInDateTime ? formatDate(vehicle.vehicleCheckInDateTime) : '-'}
                          </TableCell>

                          <TableCell sx={michiganTitlesDateCellSx}>
                            {vehicle.sentToDOTDateTime ? formatDateMoment(vehicle.sentToDOTDateTime) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>

                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          SelectProps={{ fullWidth: false }}
                          count={totalCount}
                          rowsPerPage={parameters.pageSize}
                          page={parameters.page}
                          onPageChange={(_, page) => {
                            setParameters({
                              ...parameters,
                              page
                            })
                          }}
                          onRowsPerPageChange={e => {
                            setParameters({
                              ...parameters,
                              page: 0,
                              pageSize: e.target.value as unknown as number
                            })
                          }}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Grid>

              <Grid item xs={12}>
                <GreenText variant='outlined'>
                  {`**Michigan Titles Table:**

Vehicles appear in the Michigan Titles Table when the vehicle records are created, and the Title Application State is set to Michigan.

**Check Box Selection:**

Allows users to select or deselect a vehicle.

Selecting a Vehicle adds the vehicle to the "Selected Vehicle" table section.

Only vehicles that have both their 7501 Document and CAN Ownership documents associated with the vehicle can be selected. Checking the box in the row of the vehicle moves that vehicle down into the “Selected Vehicle” section. Only one vehicle may be selected at a time.

**7501 Column:**

Displays the 7501 Document associated with the vehicle.

Clicking the File Name Downloads the 7501 Document.

If the 7501 Document has not been uploaded for the vehicle the text "MISSING" will be displayed. 

**CAN Ownership Column:**

Displays the CAN Ownership Document associated with the vehicle.

Clicking the File Name: Downloads the CAN Ownership Document.

If the CAN Ownership Document has not been uploaded for the vehicle the text "MISSING" will be displayed.`}
                </GreenText>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {selectedVehicle && (
        <Grid item xs={12}>
          <Formik
            enableReinitialize
            initialValues={selectedVehicle}
            onSubmit={(values, formikHelpers) => {
              setLoadingState('isSavingMichiganTitleApplicationPacket', true)
              formikHelpers.setSubmitting(true)
              controllers.michiganTitles
                .saveMichiganTitleApplicationPacket(values)
                .then(res => {
                  enqueueSnackbar('Michigan Title Application Packet Saved Successfully!', { variant: 'success' })
                  getMichiganTitleVehicles(parameters)

                  if (close || values.michiganTitleIsReadyForPoliceBook) {
                    setSelectedVehicle(undefined)
                    setClose(false)
                  } else {
                    setSelectedVehicle(res.value)
                  }

                  getMichiganTitleVehiclesPoliceBook()
                })
                .finally(() => {
                  setLoadingState('isSavingMichiganTitleApplicationPacket', false)
                  formikHelpers.setSubmitting(false)
                })
            }}
            validateOnBlur
            validateOnChange
          >
            {() => (
              <Form>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <SelectedVehicleCard />
                  </Grid>

                  <Grid item xs={12}>
                    <SelectedVehicleMichiganTitleApplicationPacketCard
                      close={() => {
                        setClose(true)
                      }}
                    />
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Grid>
      )}

      <Grid item xs={12}>
        <Formik
          enableReinitialize
          initialValues={michiganTitleVehiclesPoliceBook}
          onSubmit={(values, formikHelpers) => {
            formikHelpers.setSubmitting(true)
            setLoadingState('isSavingMichiganTitleVehiclesPoliceBook', true)
            controllers.michiganTitles
              .saveMichiganTitlePoliceBook(values)
              .then(res => {
                enqueueSnackbar('Police Book Saved Successfully!', { variant: 'success' })
                setMichiganTitleVehiclesPoliceBook(res.value)
                getApplicationReadyToPrint()
              })
              .finally(() => {
                formikHelpers.setSubmitting(false)
                setLoadingState('isSavingMichiganTitleVehiclesPoliceBook', false)
              })
          }}
          validateOnChange
          validateOnBlur
        >
          {() => (
            <Form>
              <PoliceBookCard />
            </Form>
          )}
        </Formik>
      </Grid>

      <Grid item xs={12}>
        <Formik
          enableReinitialize
          initialValues={applicationReadyToPrint}
          onSubmit={(values, formikHelpers) => {
            formikHelpers.setSubmitting(true)
            setLoadingState('isSavingApplicationReadyToPrint', true)
            controllers.michiganTitles
              .putApplicationReadyToPrint(values, includeApplicationPacketsReadyForMISoSBundle)
              .then(res => {
                enqueueSnackbar('Application Ready To Print Saved Successfully!', { variant: 'success' })
                setApplicationReadyToPrint(res.value)
              })
              .finally(() => {
                formikHelpers.setSubmitting(false)
                setLoadingState('isSavingApplicationReadyToPrint', false)
              })
          }}
          validateOnChange
          validateOnBlur
        >
          {() => (
            <Form>
              <ApplicationReadyToPrintCard
                includeApplicationPacketsReadyForMISoSBundle={includeApplicationPacketsReadyForMISoSBundle}
                setIncludeApplicationPacketsReadyForMISoSBundle={setIncludeApplicationPacketsReadyForMISoSBundle}
              />
            </Form>
          )}
        </Formik>
      </Grid>
    </Grid>
  )
}
